/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */
import * as z from 'zod';
/*
 * Per-user settings stored sparsely in `users.preferences`: only values the user changed are
 * saved, grouped by section (`{ appearance: { theme: 'light' } }`). Everything missing or
 * invalid resolves to the default below, so changing a default reaches everyone who never
 * overrode it, and stale values from older app versions never break the shell.
 */
export const THEME_PREFERENCES = ['system', 'dark', 'light'];
// Saved accents that are no longer offered (amber, emerald, rose) resolve to the default.
export const ACCENT_PREFERENCES = ['sky', 'indigo', 'violet', 'magenta', 'graphite'];
export const DESKTOP_BACKGROUND_PREFERENCES = ['aurora', 'subtle', 'solid'];
export const REDUCE_MOTION_PREFERENCES = ['system', 'on', 'off'];
export const DOCK_POSITIONS = [
    'bottom-center',
    'bottom-left',
    'left-top',
    'left-center',
    'left-bottom',
];
export const DOCK_REVEAL_DELAYS_MS = [0, 300, 800];
export const DOCK_ICON_SIZES = ['small', 'medium', 'large'];
export const DOCK_LABEL_PREFERENCES = ['hover', 'always', 'never'];
export const MINIMIZE_TARGETS = ['app-icon', 'dock'];
export const AGENT_KEEP_RECENT_PREFERENCES = ['never', '1h', 'today'];
/** Where the Agent chip sits: inline in the top bar (left of the shell actions, or after the
 * project picker) or floating in the bottom-right corner. */
export const AGENT_LAUNCHER_POSITIONS = ['top-right', 'top-left', 'bottom-right'];
export const AGENT_SEND_WITH_PREFERENCES = ['enter', 'mod-enter'];
const appIdList = z.array(z.string().min(1).max(64)).max(64);
/** Strict field schemas, used to validate writes. Reads wrap them with `.catch(default)`. */
const preferenceFields = {
    appearance: {
        theme: z.enum(THEME_PREFERENCES),
        accent: z.enum(ACCENT_PREFERENCES),
        background: z.enum(DESKTOP_BACKGROUND_PREFERENCES),
        reduceMotion: z.enum(REDUCE_MOTION_PREFERENCES),
    },
    dock: {
        position: z.enum(DOCK_POSITIONS),
        autoHide: z.boolean(),
        revealDelayMs: z.union(DOCK_REVEAL_DELAYS_MS.map((ms) => z.literal(ms))),
        hideOnlyWhenOverlapped: z.boolean(),
        minimizeInto: z.enum(MINIMIZE_TARGETS),
        iconSize: z.enum(DOCK_ICON_SIZES),
        showOpenIndicators: z.boolean(),
        labels: z.enum(DOCK_LABEL_PREFERENCES),
        /** App ids in the user's order. Apps missing here keep their default position. */
        order: appIdList,
        /** App ids the user removed from the dock. */
        hidden: appIdList,
    },
    agent: {
        newConversationOnOpen: z.boolean(),
        keepRecentFor: z.enum(AGENT_KEEP_RECENT_PREFERENCES),
        launcherPosition: z.enum(AGENT_LAUNCHER_POSITIONS),
        sendWith: z.enum(AGENT_SEND_WITH_PREFERENCES),
    },
};
export const DEFAULT_USER_PREFERENCES = {
    appearance: {
        theme: 'system',
        accent: 'sky',
        background: 'aurora',
        reduceMotion: 'system',
    },
    dock: {
        position: 'bottom-center',
        autoHide: false,
        revealDelayMs: 300,
        hideOnlyWhenOverlapped: true,
        minimizeInto: 'dock',
        iconSize: 'medium',
        showOpenIndicators: true,
        labels: 'hover',
        order: [],
        hidden: [],
    },
    agent: {
        newConversationOnOpen: true,
        keepRecentFor: 'never',
        launcherPosition: 'top-right',
        sendWith: 'enter',
    },
};
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
const resolvingSections = Object.fromEntries(Object.entries(preferenceFields).map(([section, fields]) => [
    section,
    z.object(Object.fromEntries(Object.entries(fields).map(([key, schema]) => [
        key,
        schema.catch(DEFAULT_USER_PREFERENCES[section][key]),
    ]))),
]));
/** Defaults merged with the user's valid overrides. Never throws. */
export const resolveUserPreferences = (stored) => {
    const values = isRecord(stored) ? stored : {};
    return Object.fromEntries(Object.entries(resolvingSections).map(([section, schema]) => [
        section,
        schema.parse(isRecord(values[section]) ? values[section] : {}),
    ]));
};
export const PREFERENCE_PATHS = Object.entries(preferenceFields).flatMap(([section, fields]) => Object.keys(fields).map((key) => `${section}.${key}`));
const isPreferencePath = (path) => PREFERENCE_PATHS.includes(path);
const fieldSchema = (path) => {
    const [section, key] = path.split('.');
    return preferenceFields[section][key];
};
/**
 * Body of `POST /api/v1/desktop/preferences`. `set` writes each path, `unset` deletes it so the
 * user falls back to the default again.
 */
export const userPreferencesPatchSchema = z
    .object({
    set: z.record(z.string(), z.unknown()).default({}),
    unset: z.array(z.string()).default([]),
})
    .superRefine((patch, ctx) => {
    for (const [path, value] of Object.entries(patch.set)) {
        if (!isPreferencePath(path)) {
            ctx.addIssue({ code: 'custom', path: ['set', path], message: 'Unknown preference' });
        }
        else if (!fieldSchema(path).safeParse(value).success) {
            ctx.addIssue({ code: 'custom', path: ['set', path], message: 'Invalid value' });
        }
    }
    for (const path of patch.unset) {
        if (!isPreferencePath(path)) {
            ctx.addIssue({ code: 'custom', path: ['unset'], message: `Unknown preference ${path}` });
        }
    }
});
/** Applies a patch to the stored (sparse) object, dropping sections that become empty. */
export const applyUserPreferencesPatch = (stored, patch) => {
    const next = Object.create(null);
    for (const [section, values] of Object.entries(stored ?? {})) {
        if (values && typeof values === 'object')
            next[section] = { ...values };
    }
    for (const [path, value] of Object.entries(patch.set ?? {})) {
        const [section, key] = path.split('.');
        next[section] = { ...next[section], [key]: value };
    }
    for (const path of patch.unset ?? []) {
        const [section, key] = path.split('.');
        if (next[section])
            delete next[section][key];
    }
    return Object.fromEntries(Object.entries(next).filter(([, values]) => Object.keys(values).length > 0));
};
/**
 * The user's dock apps: the default list with `hidden` apps removed, ordered by `order`. Apps not
 * in `order` (for example newly shipped ones) go right after the app that precedes them by default.
 */
export const resolveDockAppIds = (defaultAppIds, { order, hidden }) => {
    const known = new Set(defaultAppIds);
    const hiddenIds = new Set(hidden);
    const ordered = [...new Set(order)].filter((id) => known.has(id) && !hiddenIds.has(id));
    const placed = new Set(ordered);
    defaultAppIds.forEach((id, defaultIndex) => {
        if (placed.has(id) || hiddenIds.has(id))
            return;
        const previous = defaultAppIds
            .slice(0, defaultIndex)
            .findLast((candidate) => placed.has(candidate));
        ordered.splice(previous ? ordered.indexOf(previous) + 1 : 0, 0, id);
        placed.add(id);
    });
    return ordered;
};
//# sourceMappingURL=user-preferences.js.map